import { useState, type ReactNode } from 'react';
import { History } from 'lucide-react';
import { useAuditTrail as useDirectoryAuditTrail } from '@my/directory-api/hooks';
import { useAuditTrail as useTmsAuditTrail } from '@my/tms-api/hooks';
import { cn, HistoryDialog } from '@my/ui';

interface HistoryButtonProps {
  organisationId: string | null;
  /** e.g. "Organisation", "Resource", "CargoDefinition". */
  aggregateType: string;
  /**
   * One record's history. Pass null with `wholeType` to ask about every record
   * of the type instead — which is what a settings screen wants, since its rows
   * are edited as a set and nobody arrives knowing an id.
   */
  aggregateId?: string | null;
  wholeType?: boolean;
  /** Names the record in the dialog title: "History · Acme", "History · Users". */
  subject?: string | null;
  /** `OrderTaskCargoItem` → "Cargo", and so on per screen. */
  entityLabel?: (entityType: string) => string;
  fieldLabel?: (entityType: string, field: string) => string | null;
  /** Renders the trigger differently where a 28px icon would look out of place. */
  variant?: 'icon' | 'subtle';
  label?: ReactNode;
}

const EMPTY_MESSAGE =
  'Nothing recorded yet — the history starts from when auditing was switched on.';

function Trigger({
  variant,
  label,
  onClick,
}: {
  variant: 'icon' | 'subtle';
  label?: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label="History"
      title="Who changed what, and when"
      onClick={onClick}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-md',
        variant === 'icon'
          ? 'h-7 w-7 border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700'
          : 'px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700',
      )}
    >
      <History className="h-3.5 w-3.5" />
      {variant === 'subtle' && (label ?? 'History')}
    </button>
  );
}

function dialogTitle(subject: string | null | undefined) {
  return `History${subject ? ` · ${subject}` : ''}`;
}

/**
 * History for a record that lives in DIRECTORY — organisations, users,
 * resources, sites, parties.
 *
 * <p>
 * Two components rather than one with a `service` prop: each has to call a
 * different `useAuditTrail`, and hooks cannot be chosen at runtime. Everything
 * else about them is shared.
 * </p>
 */
export function DirectoryHistoryButton({
  organisationId,
  aggregateType,
  aggregateId = null,
  wholeType = false,
  subject,
  entityLabel,
  fieldLabel,
  variant = 'icon',
  label,
}: HistoryButtonProps) {
  const [open, setOpen] = useState(false);
  const query = useDirectoryAuditTrail(organisationId, aggregateType, aggregateId, {
    enabled: open,
    wholeType,
  });

  if (!wholeType && !aggregateId) return null;

  return (
    <>
      <Trigger variant={variant} label={label} onClick={() => setOpen(true)} />
      <HistoryDialog
        open={open}
        onClose={() => setOpen(false)}
        title={dialogTitle(subject)}
        subtitle="who changed what, and when"
        entries={query.data ?? []}
        isLoading={query.isPending}
        hasMore={query.hasNextPage}
        isLoadingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        error={query.isError ? 'Could not load this history.' : null}
        entityLabel={entityLabel}
        fieldLabel={fieldLabel}
        emptyMessage={EMPTY_MESSAGE}
      />
    </>
  );
}

/**
 * History for a screen whose records live in BOTH services — Central
 * Configuration edits cargo types and service tags (TMS) alongside equipment
 * types, carriers and routes (Directory).
 *
 * <p>
 * Two queries merged into one timeline, because the screen is one subject: an
 * operator asking what changed in the configuration does not know, and should
 * not need to know, which service owns which table. Sorted by time across both;
 * `changeSetId` grouping still holds, since a save never spans services.
 * </p>
 */
export function CombinedHistoryButton({
  organisationId,
  directoryTypes,
  tmsTypes,
  subject,
  entityLabel,
  fieldLabel,
  variant = 'icon',
  label,
}: Omit<HistoryButtonProps, 'aggregateType' | 'aggregateId' | 'wholeType'> & {
  directoryTypes: string;
  tmsTypes: string;
}) {
  const [open, setOpen] = useState(false);
  const directory = useDirectoryAuditTrail(organisationId, directoryTypes, null, {
    enabled: open,
    wholeType: true,
  });
  const tms = useTmsAuditTrail(organisationId, tmsTypes, null, {
    enabled: open,
    wholeType: true,
  });

  const directoryEntries = directory.data ?? [];
  const tmsEntries = tms.data ?? [];

  // Merging two PAGED sources needs a floor, or the middle of the list lies.
  // If Directory has fetched back to January and TMS only to June, everything
  // between them shows Directory entries and no TMS ones — which reads as "TMS
  // went quiet for five months" rather than "we have not asked yet". So the
  // merged list stops at the NEWER of the two oldest-fetched timestamps, and
  // only extends once both sides have gone further back.
  const floors = [
    directory.hasNextPage ? directoryEntries.at(-1)?.occurredUtc : undefined,
    tms.hasNextPage ? tmsEntries.at(-1)?.occurredUtc : undefined,
  ].filter((value): value is string => !!value);
  const floor = floors.length > 0 ? floors.sort().at(-1) : undefined;

  const entries = [...directoryEntries, ...tmsEntries]
    .filter((entry) => !floor || entry.occurredUtc >= floor)
    .sort((a, b) => b.occurredUtc.localeCompare(a.occurredUtc));

  const hasMore = directory.hasNextPage || tms.hasNextPage;
  const loadMore = () => {
    // Both sides, so the floor moves down together — advancing one would leave
    // the other capping the list and nothing new would appear.
    if (directory.hasNextPage) void directory.fetchNextPage();
    if (tms.hasNextPage) void tms.fetchNextPage();
  };

  return (
    <>
      <Trigger variant={variant} label={label} onClick={() => setOpen(true)} />
      <HistoryDialog
        open={open}
        onClose={() => setOpen(false)}
        title={dialogTitle(subject)}
        subtitle="who changed what, and when"
        entries={entries}
        // Both have to land before the list is honest: showing one service's
        // half while the other is still in flight reads as "this is everything".
        isLoading={directory.isPending || tms.isPending}
        hasMore={hasMore}
        isLoadingMore={directory.isFetchingNextPage || tms.isFetchingNextPage}
        onLoadMore={loadMore}
        error={directory.isError || tms.isError ? 'Could not load the full history.' : null}
        entityLabel={entityLabel}
        fieldLabel={fieldLabel}
        emptyMessage={EMPTY_MESSAGE}
      />
    </>
  );
}

/** History for a record that lives in TMS — orders, invoices, configuration. */
export function TmsHistoryButton({
  organisationId,
  aggregateType,
  aggregateId = null,
  wholeType = false,
  subject,
  entityLabel,
  fieldLabel,
  variant = 'icon',
  label,
}: HistoryButtonProps) {
  const [open, setOpen] = useState(false);
  const query = useTmsAuditTrail(organisationId, aggregateType, aggregateId, {
    enabled: open,
    wholeType,
  });

  if (!wholeType && !aggregateId) return null;

  return (
    <>
      <Trigger variant={variant} label={label} onClick={() => setOpen(true)} />
      <HistoryDialog
        open={open}
        onClose={() => setOpen(false)}
        title={dialogTitle(subject)}
        subtitle="who changed what, and when"
        entries={query.data ?? []}
        isLoading={query.isPending}
        hasMore={query.hasNextPage}
        isLoadingMore={query.isFetchingNextPage}
        onLoadMore={() => void query.fetchNextPage()}
        error={query.isError ? 'Could not load this history.' : null}
        entityLabel={entityLabel}
        fieldLabel={fieldLabel}
        emptyMessage={EMPTY_MESSAGE}
      />
    </>
  );
}
